import React, { useEffect, useState, useRef } from "react";
import { Hooks, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { getUrl } = Util;
import { get_strategies, get_strategyReturnTrend } from "@/api/aicopy";
import ReturnRateChart from "@/components/Pages/aicopy/components/ReturnRateChart";
import { getProfitColorClass } from "@/components/Pages/aicopy/utils/colorScheme";
import aicopyStyles from "@/components/Pages/aicopy/index.module.less";
import strategyIconAlpha10 from "assets/images/aicopy/eb3a5f42-0255-4b85-82c6-bd5462a3928b.png";
import arrowRight from "assets/images/aicopy/898172df-f017-4eff-be9d-a2b6fe27e1fa.svg";
import tagBackground from "assets/images/aicopy/7e4271f1-feec-44a0-9dcc-aeec6f8ed213.svg";
import tagIcon from "assets/images/aicopy/ae0a5b7b-f195-4586-9300-03ed4ed14813.svg";
import rowStyles from "./LandingAiStrategyList.module.less";

function normalizeStrategiesResponse(response) {
  if (!response) return [];
  if (Array.isArray(response.result)) return response.result;
  if (response.rc === 0 && Array.isArray(response.result)) return response.result;
  if (response.code === 0 && Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
}

/**
 * 落地页 Markets 区块「AI Trading」Tab：与 /aicopy 策略列表同源接口；卡片含描述折叠（无跟单表单）
 */
export default function LandingAiStrategyList() {
  const t = useTranslation();
  const [strategies, setStrategies] = useState([]);
  const [strategyReturnTrends, setStrategyReturnTrends] = useState({});
  const [expandedDescId, setExpandedDescId] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  const loadStrategyReturnTrend = async (strategyId) => {
    try {
      const response = await get_strategyReturnTrend(strategyId, 30, { isResFull: true });
      if (response.rc === 0 && response.result) {
        let trendData = [];
        if (Array.isArray(response.result)) {
          trendData = response.result;
        } else if (response.result) {
          trendData = [response.result];
        }
        setStrategyReturnTrends((prev) => ({ ...prev, [strategyId]: trendData }));
      }
    } catch (e) {
      console.error(`landing ai strategy trend ${strategyId}`, e);
    }
  };

  useEffect(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    (async () => {
      try {
        const response = await get_strategies({
          params: { limit: 3, offset: 0 },
          isResFull: true,
        });
        const list = normalizeStrategiesResponse(response);
        setStrategies(list.slice(0, 3));
        list.forEach((s) => {
          if (s?.strategyId) loadStrategyReturnTrend(s.strategyId);
        });
      } catch (e) {
        console.error("landing get_strategies", e);
        setStrategies([]);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    })();
  }, []);

  const goStrategy = (strategyId) => {
    if (!strategyId) return;
    window.location.href = getUrl(`/aicopy/${strategyId}`);
  };

  const formatReturnRate = (raw) => {
    const s = String(raw ?? "")
      .replace(/%/g, "")
      .trim();
    const n = parseFloat(s);
    if (Number.isNaN(n)) return "0%";
    if (n > 0) return `+${s}%`;
    if (n < 0) return `${s}%`;
    return "0%";
  };

  return (
    <div className={`${aicopyStyles.aiCopy} ${rowStyles.embed}`}>
      <div className={rowStyles.row}>
        {strategies.map((strategy) => (
          <div key={strategy.strategyId} className={rowStyles.cardSlot}>
            <div className={`${aicopyStyles.strategyCard} ${rowStyles.cardFill}`}>
              <div
                className={aicopyStyles.strategyHeader}
                onClick={() => goStrategy(strategy.strategyId)}
                role="presentation"
              >
                <div className={aicopyStyles.strategyInfo}>
                  <div className={aicopyStyles.strategyIconWrapper}>
                    <img
                      src={strategy.avatarUrl || strategyIconAlpha10}
                      alt={strategy.strategyName}
                      className={aicopyStyles.strategyIcon}
                    />
                  </div>
                  <div className={aicopyStyles.strategyNameWrapper}>
                    <span className={aicopyStyles.strategyName}>{strategy.strategyName}</span>
                    <div className={aicopyStyles.arrowIcon}>
                      <img src={arrowRight} alt="" />
                    </div>
                  </div>
                  <div className={aicopyStyles.strategyTag}>
                    {strategy.tags && strategy.tags.length > 0
                      ? strategy.tags.map((tag, index) => (
                          <div key={index} className={aicopyStyles.tagItem}>
                            <div className={aicopyStyles.tagBg}>
                              <img src={tagBackground} alt="" className={aicopyStyles.tagBgImg} />
                            </div>
                            <div className={aicopyStyles.tagIcon}>
                              <img src={tagIcon} alt="" />
                            </div>
                            <span className={aicopyStyles.tagText}>{tag}</span>
                          </div>
                        ))
                      : null}
                  </div>
                </div>
              </div>

              <div className={aicopyStyles.strategyStats}>
                <div className={aicopyStyles.strategyStatRow}>
                  <div className={`${aicopyStyles.strategyReturnRate} ${rowStyles.returnRateCol}`}>
                    <div
                      className={`${aicopyStyles.returnRateValue} ${getProfitColorClass(
                        strategy.totalReturnRate,
                        aicopyStyles
                      )}`}
                    >
                      {formatReturnRate(strategy.totalReturnRate)}
                    </div>
                    <div className={aicopyStyles.returnRateLabel}>{t("aicopy.returnRate")}</div>
                  </div>
                  <div className={aicopyStyles.strategyChart}>
                    {strategyReturnTrends[strategy.strategyId] &&
                    strategyReturnTrends[strategy.strategyId].length > 0 ? (
                      <ReturnRateChart data={strategyReturnTrends[strategy.strategyId]} />
                    ) : null}
                  </div>
                </div>
                <div className={aicopyStyles.strategyStatRow}>
                  <div className={aicopyStyles.strategyStatItem}>
                    <div className={aicopyStyles.strategyStatValue}>{strategy.aum || "0"}</div>
                    <div className={aicopyStyles.strategyStatLabel}>{t("aicopy.assetsUnderManagement")}</div>
                  </div>
                  <div className={aicopyStyles.strategyStatItem}>
                    <div className={aicopyStyles.strategyStatValue}>{strategy.followerCount || "0"}</div>
                    <div className={aicopyStyles.strategyStatLabel}>{t("aicopy.userCount")}</div>
                  </div>
                  <div className={aicopyStyles.strategyStatItem}>
                    <div
                      className={`${aicopyStyles.strategyStatValue} ${getProfitColorClass(
                        strategy.totalProfit,
                        aicopyStyles
                      )}`}
                    >
                      {strategy.totalProfit ?? "0.00"}
                    </div>
                    <div className={aicopyStyles.strategyStatLabel}>{t("aicopy.strategyPnl")}</div>
                  </div>
                </div>
              </div>

              {strategy.description ? (
                <div
                  className={aicopyStyles.strategyDescWrap}
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <div
                    className={
                      expandedDescId === strategy.strategyId
                        ? aicopyStyles.strategyDescriptionExpanded
                        : aicopyStyles.strategyDescription
                    }
                  >
                    {strategy.description}
                  </div>
                  <span
                    className={aicopyStyles.descToggle}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedDescId(
                        expandedDescId === strategy.strategyId ? null : strategy.strategyId
                      );
                    }}
                    role="button"
                  >
                    {expandedDescId === strategy.strategyId
                      ? t("console.put.away")
                      : t("console.expand")}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {loading && <div className={rowStyles.status}>{t("aicopy.loading")}</div>}
      {!loading && strategies.length === 0 && <div className={rowStyles.status}>{t("aicopy.noData")}</div>}
    </div>
  );
}
